'use client'

import { useAtomValue } from 'jotai'
import { selectAtom } from 'jotai/utils'
import { createContext, type ReactNode, useContext, useMemo } from 'react'

import { type WatchListTickerMap, watchListTickerMapAtom } from '@/state/dashboard'

import type { WatchlistRow } from './use-stable-watchlist-rows'

export type LiveWatchlistTicker = WatchListTickerMap[string]

const EMPTY_TICKER: LiveWatchlistTicker = {}

/**
 * Subscribes to exactly one symbol's slot in `watchListTickerMapAtom` — re-
 * renders only when THAT slot's object reference changes. `scheduleTickerMapWrite`
 * (state/dashboard.ts) only replaces a symbol's entry reference when something in
 * it actually changed, so this skips re-rendering for every OTHER symbol's flush,
 * unlike subscribing to the whole map. Same `useMemo(() => selectAtom(...), [key])`
 * pattern already used elsewhere against this atom (e.g.
 * order-form/collapsible/exit-condition.tsx) — kept at the existing ~100ms
 * coalescing interval, not a new/separate throttle.
 */
export function useWatchlistLiveTicker(symbolKey: string): LiveWatchlistTicker {
  const tickerAtom = useMemo(
    () => selectAtom(watchListTickerMapAtom, (map) => map[symbolKey] ?? EMPTY_TICKER, Object.is),
    [symbolKey]
  )
  return useAtomValue(tickerAtom)
}

const shallowEqualTickerSubset = (a: WatchListTickerMap, b: WatchListTickerMap): boolean => {
  const aKeys = Object.keys(a)
  if (aKeys.length !== Object.keys(b).length) return false
  return aKeys.every((key) => a[key] === b[key])
}

/**
 * Batched form of `useWatchlistLiveTicker` for a whole visible list: ONE
 * `selectAtom` subscription scoped to `symbolKeys`, instead of every row
 * mounting its own. A virtualized list that gave each rendered row its own
 * subscription (watchlist wizard's master/detail rows used to) meant every
 * tick of `watchListTickerMapAtom` — for ANY symbol, wizard-relevant or not —
 * re-evaluated one selector per on-screen row for as long as the list stayed
 * mounted. Callers should pass only the current visible range (+ a small
 * scroll-ahead offset), not the full data set — the derived atom is rebuilt
 * whenever the key set changes, so an unbounded key list defeats the point.
 */
export function useWatchlistLiveTickers(symbolKeys: string[]): WatchListTickerMap {
  const keysSignature = symbolKeys.join('|')
  const tickerAtom = useMemo(
    () =>
      selectAtom(
        watchListTickerMapAtom,
        (map) => {
          const subset: WatchListTickerMap = {}
          for (const key of symbolKeys) {
            const ticker = map[key]
            if (ticker) subset[key] = ticker
          }
          return subset
        },
        shallowEqualTickerSubset
      ),
    // symbolKeys is intentionally summarized by keysSignature — see the doc
    // comment above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [keysSignature]
  )
  return useAtomValue(tickerAtom)
}

const WatchlistLiveTickerContext = createContext<LiveWatchlistTicker>(EMPTY_TICKER)

/** Read the live ticker for the row currently being rendered — only valid inside
 *  a cell rendered under `WatchlistTickerRowWrapper` (i.e. as a DataTable
 *  `rowWrapper`). Falls back to an empty ticker outside that context rather than
 *  throwing, since cell renderers may be unit-tested/previewed standalone. */
export function useWatchlistLiveTickerContext(): LiveWatchlistTicker {
  return useContext(WatchlistLiveTickerContext)
}

/**
 * DataTable `rowWrapper` — subscribes this one row to its own live ticker
 * (independent of the row's own `data` identity, so a memoized/stabilized row
 * still gets fresh prices) and provides it to the row's cells via context.
 */
export function WatchlistTickerRowWrapper({
  row,
  children,
}: {
  row: WatchlistRow
  children: ReactNode
}) {
  const liveTicker = useWatchlistLiveTicker(row.id)
  return (
    <WatchlistLiveTickerContext.Provider value={liveTicker}>
      {children}
    </WatchlistLiveTickerContext.Provider>
  )
}
